<template>
  <BaseModal
    :is-open="isOpen"
    :role="notification.blocking ? 'alertdialog' : 'dialog'"
    :aria-labelledby="headingId"
    :aria-describedby="bodyId"
    :should-close-on-escape="!notification.blocking"
    :should-close-on-overlay-click="!notification.blocking"
    overlay-color="rgba(255, 255, 255, 0.8)"
    @request-close="onModalRequestClose"
  >
    <div :class="$style.modalContent">
      <section
        :class="$style.content"
        data-site-notification-modal
        :data-site-notification-id="notification.id"
      >
        <header v-if="!notification.blocking" :class="$style.toolbar">
          <button
            type="button"
            :class="$style.dismiss"
            data-site-notification-dismiss
            :aria-label="dismissLabel"
            @click="dismiss"
          >
            <IconClose width="24" height="24" />
          </button>
        </header>

        <div :class="$style.contentBody">
          <h2 :id="headingId" :class="$style.heading">
            {{ notification.heading }}
          </h2>

          <MarkedParser
            :id="bodyId"
            :class="$style.body"
            :md="notification.body"
          />

          <div v-if="ctas.length || notification.blocking" :class="$style.ctas">
            <CallToAction
              v-for="(cta, index) in ctas"
              :key="ctaKey(cta, index)"
              :cta="cta"
              :parent-entry-title="notification.entryTitle"
            />
            <BaseButton
              v-if="notification.blocking && loggedIn"
              type="button"
              data-site-notification-logout
              :class="$style.logout"
              @click="logout"
            >
              {{ localizedStrings['log-out'] || 'Log Out' }}
            </BaseButton>
          </div>
        </div>
      </section>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { TypeCallToActionWithoutUnresolvableLinksResponse } from '~/types/contentful'
import { useWizardsLogout } from '~/composables/useWizardsLogout'
import type { SiteNotification } from '~/types/siteNotification'

const props = withDefaults(
  defineProps<{
    notification: SiteNotification
    isOpen?: boolean
    loggedIn?: boolean
  }>(),
  { isOpen: true, loggedIn: true },
)

const emit = defineEmits<{
  dismiss: []
}>()

const { localizedStrings } = useLocalizedStrings()
const { logout } = useWizardsLogout()

const idPart = computed(() =>
  props.notification.id.replace(/[^a-zA-Z0-9_-]/g, '-'),
)
const headingId = computed(() => `site-notification-${idPart.value}-heading`)
const bodyId = computed(() => `site-notification-${idPart.value}-body`)
const dismissLabel = computed(
  () =>
    props.notification.dismissLabel.trim() ||
    localizedStrings.value['close-modal'] ||
    'Close Modal',
)
const ctas = computed(
  () =>
    props.notification
      .ctas as unknown as TypeCallToActionWithoutUnresolvableLinksResponse[],
)
const ctaKey = (
  cta: TypeCallToActionWithoutUnresolvableLinksResponse,
  index: number,
) =>
  cta.sys?.id ||
  (cta as unknown as { id?: string }).id ||
  `site-notification-cta-${index}`

const dismiss = () => {
  if (!props.notification.blocking) emit('dismiss')
}

const onModalRequestClose = () => dismiss()
</script>

<style lang="scss" module>
.content {
  position: relative;
  width: min(640px, calc(100vw - 32px));
  max-height: calc(100vh - 64px);
  margin: 0;
  padding: 0;
  overflow: auto;
  color: var(--black);
  background: var(--white);
  border-bottom: 4px solid var(--purple);
  border-radius: 0;
  box-shadow: 0 12px 45px rgba(0, 0, 0, 0.3);
  pointer-events: auto;
}

.modalContent {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 100%;
  padding: 32px;
  pointer-events: none;
}

.toolbar {
  display: flex;
  box-sizing: border-box;
  align-items: center;
  justify-content: flex-end;
  height: 48px;
  min-height: 48px;
  padding: 0;
  background: var(--purple-dark);
  border-radius: 0;
}

.contentBody {
  padding: 32px;
}

.heading {
  margin-bottom: 20px;
  font-family: var(--roboto-condensed);
  font-size: 30px;
  line-height: 1.1;
  text-wrap: balance;
}

.dismiss {
  flex: 0 0 auto;
  display: grid;
  width: 48px;
  height: 48px;
  padding: 0;
  color: var(--white);
  background: transparent;
  border: 0;
  border-radius: 0;
  outline: none;
  cursor: pointer;
  place-items: center;
}

.dismiss:focus-visible {
  background: transparent;
  opacity: 0.8;
}

.body {
  line-height: 1.5;

  :global(h1),
  :global(h2),
  :global(h3),
  :global(h4),
  :global(h5),
  :global(h6),
  :global(p),
  :global(ul),
  :global(ol) {
    margin-bottom: 12px;
  }
}

.ctas {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
}

.logout {
  --button-background: var(--gray-8);
  --button-background--hover: var(--gray-7);
}

@media (max-width: 640px) {
  .content {
    width: 100%;
    max-height: calc(100vh - 24px);
  }

  .contentBody {
    padding: 24px 20px;
  }

  .modalContent {
    padding: 12px;
  }

  .heading {
    font-size: 26px;
  }
}
</style>
